import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';

export type OpenPullRequestProvider = 'GITHUB' | 'GITLAB' | 'BITBUCKET';

export type OpenPullRequestItem = {
  provider: OpenPullRequestProvider;
  projectId: string;
  projectName: string;
  repositoryId: string;
  repositoryName: string;
  number: number;
  title: string;
  url: string;
  author: string | null;
  isDraft: boolean | null;
  sourceBranch: string | null;
  targetBranch: string | null;
  createdAt: string;
  updatedAt: string;
};

export type OpenPullRequestFailureReason =
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'RATE_LIMITED'
  | 'TIMEOUT'
  | 'RECONNECT_REQUIRED'
  | 'UNSUPPORTED_REMOTE'
  | 'REPOSITORY_LIMIT_EXCEEDED'
  | 'UNKNOWN';

export type OpenPullRequestFailure = {
  provider: OpenPullRequestProvider;
  projectId: string;
  repositoryId: string;
  repositoryName: string | null;
  reason: OpenPullRequestFailureReason;
};

export type OpenPullRequestsResponse = {
  items: OpenPullRequestItem[];
  failures: OpenPullRequestFailure[];
  meta: {
    requestedProjectCount: number;
    accessibleProjectCount: number;
    repositoryCount: number;
    connectedRepositoryCount: number;
    successfulRepositoryCount: number;
    providers: OpenPullRequestProvider[];
    partialFailureCount: number;
  };
};

export const fetchOpenPullRequests = async (
  token: string,
  projectIds: string[],
  limit = 20,
): Promise<OpenPullRequestsResponse> => {
  const query = new URLSearchParams();
  const normalizedProjectIds = [...new Set(projectIds.filter(Boolean))].sort();
  if (normalizedProjectIds.length > 0) {
    query.set('projectIds', normalizedProjectIds.join(','));
  }
  query.set('limit', String(limit));

  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/me/open-pull-requests?${query.toString()}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch open pull requests: ${response.status}`);
  }

  const body = (await response.json()) as { data: OpenPullRequestsResponse };
  return body.data;
};
